import React, { useState } from 'react';
import './DeveloperPortal.css';

const DATA_BASE = 'https://peakdata-1048592730476.europe-west4.run.app';
const MESSAGING_BASE = 'https://messaging-peak-1048592730476.europe-west4.run.app';
const EXAMPLE_ORGANISATION_ID = 'your-organisation-id';
const EXAMPLE_SENDER_ID = 'your-sender-id';

const endpoints = [
  {
    id: 'login',
    group: 'Authentication',
    name: 'Create access token',
    description: 'Exchange your Peak account credentials for an access token.',
    method: 'POST',
    baseUrl: DATA_BASE,
    path: '/public/login',
    requiresAuth: false,
    body: {
      username: 'developer@company.com',
      password: 'your-password',
    },
    success: {
      access_token: 'YOUR_ACCESS_TOKEN_FROM_LOGIN_RESPONSE',
      expires_in: 300,
      refresh_expires_in: 1800,
      token_type: 'Bearer',
      scope: 'openid profile email',
    },
  },
  {
    id: 'send-data',
    group: 'Data rewards',
    name: 'Send a data reward',
    description: 'Dispatch a mobile data bundle to one recipient.',
    method: 'POST',
    baseUrl: DATA_BASE,
    path: '/api/v2/organization/{orgId}/reward',
    requiresAuth: true,
    body: {
      request_id: 'your-unique-request-id',
      bundle_amount: '20',
      msisdn: '254700000000',
      sender_id: null,
      message: 'You have received a data reward from Peak.',
      postpay: false,
    },
    success: {
      id: 1234,
      bundle_amount: '20',
      status: 'SUCCESS',
      status_desc: '',
      request_id: 'your-unique-request-id',
      response_id: 'peak-response-id',
      msisdn: '254700000000',
      postpay: false,
    },
  },
  {
    id: 'list-data',
    group: 'Data rewards',
    name: 'List data rewards',
    description: 'Retrieve data rewards previously dispatched by an organisation.',
    method: 'GET',
    baseUrl: DATA_BASE,
    path: '/api/v2/organization/{orgId}/reward',
    requiresAuth: true,
    body: null,
    success: {
      count: 1,
      data: [
        {
          id: 1234,
          bundle_amount: '20',
          status: 'SUCCESS',
          request_id: 'your-unique-request-id',
          created_at: '2026-01-15T09:30:00.000000Z',
        },
      ],
    },
  },
  {
    id: 'send-airtime',
    group: 'Airtime rewards',
    name: 'Send airtime',
    description: 'Create an airtime reward for a recipient in your organisation.',
    method: 'POST',
    baseUrl: DATA_BASE,
    path: '/api/v2/organization/{orgId}/reward',
    requiresAuth: true,
    body: {
      request_id: 'your-unique-request-id',
      bundle_amount: '20',
      msisdn: '254700000000',
      sender_id: null,
      message: 'You have received an airtime reward from Peak.',
      postpay: false,
    },
    success: {
      id: 1234,
      bundle_amount: '20',
      status: 'SUCCESS',
      request_id: 'your-unique-request-id',
      msisdn: '254700000000',
    },
  },
  {
    id: 'list-airtime',
    group: 'Airtime rewards',
    name: 'List airtime rewards',
    description: 'Retrieve airtime reward activity for an organisation.',
    method: 'GET',
    baseUrl: DATA_BASE,
    path: '/api/v2/organization/{orgId}/airtime',
    requiresAuth: true,
    body: null,
    success: {
      count: 1,
      data: [
        {
          id: 1234,
          bundle_amount: '20',
          status: 'SUCCESS',
          request_id: 'your-unique-request-id',
          contact: {
            mobile_no: '254700000000',
            status: 'ACTIVE',
          },
        },
      ],
    },
  },
  {
    id: 'send-sms',
    group: 'Messaging',
    name: 'Send an SMS',
    description: 'Send one transactional message using an approved Sender ID.',
    method: 'POST',
    baseUrl: MESSAGING_BASE,
    path: '/api/v1/message/{sid}/user/send',
    requiresAuth: true,
    body: {
      destination: '254700000000',
      content: 'Your Peak verification code is 482903.',
      requestid: 'your-unique-request-id',
      scheduled: '2026-01-15T09:30:00.000Z',
      channel: 'PEAK',
      organization_id: EXAMPLE_ORGANISATION_ID,
    },
    success: {
      id: 1024,
      requestid: 'your-unique-request-id',
      destination: '254700000000',
      source: 'PEAK',
      status: 'QUEUED',
      direction: 'OUTBOUND',
      channel: 'PEAK',
    },
  },
  {
    id: 'send-bulk-sms',
    group: 'Messaging',
    name: 'Send bulk SMS',
    description: 'Broadcast a message to a saved contact group.',
    method: 'POST',
    baseUrl: MESSAGING_BASE,
    path: '/api/message/{sid}/broadcast/send',
    requiresAuth: true,
    body: {
      name: 'Customer update',
      group_id: 239,
      description: 'Example campaign',
      service_id: 54,
      requestid: 'your-unique-request-id',
      content: 'Your message from Peak.',
      scheduled: '2026-01-15T09:30:00.000Z',
      channel: 'PEAK',
      organization_id: EXAMPLE_ORGANISATION_ID,
    },
    success: {
      id: 120,
      name: 'Customer update',
      group_id: 239,
      channel: 'PEAK',
      status: 'QUEUED',
    },
  },
];

const groups = [...new Set(endpoints.map((endpoint) => endpoint.group))];
const codeLanguages = [
  { value: 'curl', label: 'cURL' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'node', label: 'Node.js' },
  { value: 'go', label: 'Go' },
];

const getExampleUrl = (endpoint) =>
  `${endpoint.baseUrl}${endpoint.path
    .replace('{orgId}', EXAMPLE_ORGANISATION_ID)
    .replace('{sid}', EXAMPLE_SENDER_ID)}`;

const buildExampleCurl = (endpoint) => {
  const pieces = [
    `curl --request ${endpoint.method}`,
    `  --url '${getExampleUrl(endpoint)}'`,
    `  --header 'Content-Type: application/json'`,
  ];

  if (endpoint.requiresAuth) {
    pieces.push(`  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`);
  }

  if (endpoint.body) {
    const exampleBody = JSON.stringify(endpoint.body, null, 2).replace(/'/g, "'\\''");
    pieces.push(`  --data '${exampleBody}'`);
  }

  return pieces.join(' \\\n');
};

const buildPythonExample = (endpoint) => {
  const headers = [
    'headers = {',
    '    "Content-Type": "application/json",',
    ...(endpoint.requiresAuth
      ? ['    "Authorization": "Bearer YOUR_ACCESS_TOKEN",']
      : []),
    '}',
  ];
  const lines = [
    ...(endpoint.body ? ['import json'] : []),
    'import requests',
    '',
    `url = "${getExampleUrl(endpoint)}"`,
    ...headers,
  ];

  if (endpoint.body) {
    lines.push(
      `payload = json.loads(r'''${JSON.stringify(endpoint.body, null, 2)}''')`,
      '',
      `response = requests.request("${endpoint.method}", url, headers=headers, json=payload)`
    );
  } else {
    lines.push('', `response = requests.request("${endpoint.method}", url, headers=headers)`);
  }

  lines.push('response.raise_for_status()', 'print(response.json())');
  return lines.join('\n');
};

const buildJavaExample = (endpoint) => {
  const requestMethod = endpoint.body
    ? `.POST(HttpRequest.BodyPublishers.ofString(requestBody))`
    : '.GET()';
  const lines = [
    'import java.net.URI;',
    'import java.net.http.HttpClient;',
    'import java.net.http.HttpRequest;',
    'import java.net.http.HttpResponse;',
    '',
    'public class PeakApiExample {',
    '  public static void main(String[] args) throws Exception {',
    '    HttpClient client = HttpClient.newHttpClient();',
  ];

  if (endpoint.body) {
    lines.push(`    String requestBody = ${JSON.stringify(JSON.stringify(endpoint.body))};`);
  }

  lines.push(
    '    HttpRequest request = HttpRequest.newBuilder()',
    `        .uri(URI.create("${getExampleUrl(endpoint)}"))`,
    '        .header("Content-Type", "application/json")'
  );

  if (endpoint.requiresAuth) {
    lines.push('        .header("Authorization", "Bearer YOUR_ACCESS_TOKEN")');
  }

  lines.push(
    `        ${requestMethod}`,
    '        .build();',
    '',
    '    HttpResponse<String> response = client.send(',
    '        request, HttpResponse.BodyHandlers.ofString());',
    '    System.out.println(response.body());',
    '  }',
    '}'
  );

  return lines.join('\n');
};

const buildNodeExample = (endpoint) => {
  const lines = [
    `const response = await fetch('${getExampleUrl(endpoint)}', {`,
    `  method: '${endpoint.method}',`,
    '  headers: {',
    "    'Content-Type': 'application/json',",
  ];

  if (endpoint.requiresAuth) {
    lines.push("    'Authorization': 'Bearer YOUR_ACCESS_TOKEN',");
  }

  lines.push('  },');

  if (endpoint.body) {
    lines.push(`  body: JSON.stringify(${JSON.stringify(endpoint.body, null, 2)}),`);
  }

  lines.push(
    '});',
    '',
    "if (!response.ok) throw new Error('Request failed: ' + response.status);",
    'const data = await response.json();',
    'console.log(data);'
  );

  return lines.join('\n');
};

const buildGoExample = (endpoint) => {
  const imports = [
    ...(endpoint.body ? ['\t"bytes"'] : []),
    '\t"fmt"',
    '\t"io"',
    '\t"net/http"',
  ];
  const bodyArgument = endpoint.body ? 'bytes.NewBuffer(payload)' : 'nil';
  const lines = [
    'package main',
    '',
    'import (',
    ...imports,
    ')',
    '',
    'func main() {',
    `\turl := "${getExampleUrl(endpoint)}"`,
  ];

  if (endpoint.body) {
    lines.push(`\tpayload := []byte(\`${JSON.stringify(endpoint.body, null, 2)}\`)`);
  }

  lines.push(
    `\treq, err := http.NewRequest("${endpoint.method}", url, ${bodyArgument})`,
    '\tif err != nil {',
    '\t\tpanic(err)',
    '\t}',
    '',
    '\treq.Header.Set("Content-Type", "application/json")'
  );

  if (endpoint.requiresAuth) {
    lines.push('\treq.Header.Set("Authorization", "Bearer YOUR_ACCESS_TOKEN")');
  }

  lines.push(
    '',
    '\tresponse, err := http.DefaultClient.Do(req)',
    '\tif err != nil {',
    '\t\tpanic(err)',
    '\t}',
    '\tdefer response.Body.Close()',
    '',
    '\tresponseBody, err := io.ReadAll(response.Body)',
    '\tif err != nil {',
    '\t\tpanic(err)',
    '\t}',
    '\tfmt.Println(string(responseBody))',
    '}'
  );

  return lines.join('\n');
};

const buildCodeExample = (endpoint, language) => {
  switch (language) {
    case 'python':
      return buildPythonExample(endpoint);
    case 'java':
      return buildJavaExample(endpoint);
    case 'node':
      return buildNodeExample(endpoint);
    case 'go':
      return buildGoExample(endpoint);
    default:
      return buildExampleCurl(endpoint);
  }
};

const DeveloperPortal = () => {
  const [activeEndpointId, setActiveEndpointId] = useState('send-data');
  const [codeLanguage, setCodeLanguage] = useState('curl');
  const [copyState, setCopyState] = useState('idle');
  const endpoint = endpoints.find((item) => item.id === activeEndpointId);
  const requestUrl = getExampleUrl(endpoint);
  const codeExample = buildCodeExample(endpoint, codeLanguage);

  const scrollToDocumentation = () => {
    document.getElementById('api-documentation')?.scrollIntoView({ behavior: 'smooth' });
  };

  const selectEndpoint = (id) => {
    setActiveEndpointId(id);
    setCopyState('idle');
  };

  const selectLanguage = (event) => {
    setCodeLanguage(event.target.value);
    setCopyState('idle');
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(codeExample);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1600);
    } catch (error) {
      setCopyState('error');
    }
  };

  return (
    <div className="developer-portal">
      <section className="developer-hero">
        <div className="developer-hero-content">
          <span className="developer-eyebrow">DEVELOPERS</span>
          <h1>Peak Mobile APIs</h1>
          <p>
            Explore how to integrate mobile data, airtime rewards, and SMS into your
            products using our simple APIs.
          </p>
          <div className="developer-hero-actions">
            <button
              type="button"
              className="developer-primary-button"
              onClick={scrollToDocumentation}
            >
              API documentation
            </button>
            <a className="developer-secondary-button" href="#getting-started">
              Getting started
            </a>
          </div>
        </div>
      </section>

      <section className="developer-quickstart" id="getting-started">
        <div className="developer-section-heading">
          <span>GETTING STARTED</span>
          <h2>Use the APIs from your own client</h2>
          <p>Follow these steps to authenticate and make your first request.</p>
        </div>
        <div className="developer-steps">
          <article>
            <span className="developer-step-number">01</span>
            <h3>Request a token</h3>
            <p>
              Call the login endpoint from your secure API client and copy the
              <code className="developer-inline-code">access_token</code> from its response.
            </p>
            <code>POST /public/login</code>
          </article>
          <article>
            <span className="developer-step-number">02</span>
            <h3>Choose a service</h3>
            <p>Choose Data Rewards, Airtime Rewards, or SMS for your integration.</p>
            <code>Data · Airtime · SMS</code>
          </article>
          <article>
            <span className="developer-step-number">03</span>
            <h3>Send from your app</h3>
            <p>
              Add the token as a Bearer header, replace the placeholders, and send the
              request from your own environment.
            </p>
            <code>Authorization: Bearer YOUR_ACCESS_TOKEN</code>
          </article>
        </div>
      </section>

      <section className="api-demo-section" id="api-documentation">
        <div className="developer-section-heading demo-heading">
          <span>DEVELOPER RESOURCES</span>
          <h2>API documentation</h2>
          <p>
            Select an endpoint to view its request format, code examples, and response.
          </p>
        </div>

        <div className="api-demo-shell">
          <aside className="demo-sidebar">
            <div className="demo-sidebar-title">Services</div>
            {groups.map((group) => (
              <div className="demo-endpoint-group" key={group}>
                <p>{group}</p>
                {endpoints
                  .filter((item) => item.group === group)
                  .map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className={item.id === activeEndpointId ? 'active' : ''}
                      onClick={() => selectEndpoint(item.id)}
                      aria-pressed={item.id === activeEndpointId}
                    >
                      <span className={`method-pill method-${item.method.toLowerCase()}`}>
                        {item.method}
                      </span>
                      <span>{item.name}</span>
                    </button>
                  ))}
              </div>
            ))}
          </aside>

          <div className="demo-main">
            <div className="demo-request-heading">
              <div>
                <span className={`method-pill method-${endpoint.method.toLowerCase()}`}>
                  {endpoint.method}
                </span>
                <h3>{endpoint.name}</h3>
              </div>
            </div>
            <p className="demo-description">{endpoint.description}</p>

            <div className="request-url">
              <span>{endpoint.method}</span>
              <code>{requestUrl}</code>
            </div>

            <div className="demo-auth-guide">
              <span>Authentication</span>
              {endpoint.id === 'login' ? (
                <p>
                  Copy the <code>access_token</code> returned in the login response and use
                  it to authenticate your other API requests.
                </p>
              ) : (
                <p>
                  Call <code>POST /public/login</code>, copy the <code>access_token</code>
                  from the response, and use it as the Bearer token in this request.
                </p>
              )}
            </div>

            <div className="demo-code-panel">
              <div className="editor-label">
                <span>Code example</span>
                <div className="code-example-actions">
                  <select
                    value={codeLanguage}
                    onChange={selectLanguage}
                    aria-label="Code language"
                  >
                    {codeLanguages.map((language) => (
                      <option key={language.value} value={language.value}>
                        {language.label}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={copyCode}>
                    {copyState === 'copied'
                      ? 'Copied!'
                      : copyState === 'error'
                        ? 'Copy failed'
                        : 'Copy code'}
                  </button>
                </div>
              </div>
              <pre>
                <code>{codeExample}</code>
              </pre>
            </div>
          </div>

          <div className="demo-response">
            <div className="response-header">
              <span>Sample response</span>
            </div>
            <pre>
              <code>{JSON.stringify(endpoint.success, null, 2)}</code>
            </pre>
          </div>
        </div>
      </section>

      <section className="developer-sender-note">
        <div>
          <span>SMS REQUIREMENT</span>
          <h2>Apply for a Sender ID</h2>
          <p>
            Messaging requests require an approved Sender ID. Apply from your Peak dashboard
            under <b>Settings → Sender ID</b> before using the SMS endpoints.
          </p>
        </div>
        <a href="https://app.peakmobile.co.ke/signin" target="_blank" rel="noopener noreferrer">
          Open Peak dashboard
        </a>
      </section>
    </div>
  );
};

export default DeveloperPortal;
